import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, Select, Skeleton, Tab, Tabs } from "@mui/material";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import BranchComparisonDialog from "../components/dashboard/BranchComparisonDialog";
import type { BranchCompareTourPhase } from "../components/dashboard/BranchComparisonDialog";
import EmptyState from "../components/EmptyState";
import PageHeader from "../components/PageHeader";
import SectionHeader from "../components/SectionHeader";
import { LazyZeroMap, type MapMarker } from "../components/map";
import KpiFigure from "../components/metrics/KpiFigure";
import MetricStrip from "../components/metrics/MetricStrip";
import BranchTable from "../components/operations/BranchTable";
import { SetupCoachmark } from "../components/selfSetup/SetupCoachmark";
import {
  HELP_GUIDE_BRANCH_ANALYSIS,
  HELP_GUIDE_COMPARE_BRANCHES,
} from "../constants/knowledgeBaseTryIt";
import { ZERO_ICONS } from "../constants/zeroIcons";
import { useNumberFormat } from "../hooks/useNumberFormat";
import { useSelfSetupTour } from "../hooks/useSelfSetupTour";
import { helpI18n } from "../i18n/help";
import { useDashboardStore } from "../stores/dashboard.store";
import { useOrganizationStore } from "../stores/organization.store";
import { contentBorders } from "../theme/createContentTheme";
import { OrganizationType } from "../types/organization.type";
import type { BranchOperationalRow } from "../types/dashboard.type";
import { useAuthPermissions } from "../hooks/useAuthPermissions";
import { useMonthPickupOrders } from "../hooks/useOperationsData";
import { orgScopedPermission } from "../auth/jwt";
import { orderCompletionBySite } from "../utils/branchOrders.helpers";
import {
  ALL_TIME_MONTH_VALUE,
  buildDashboardMonthOptions,
  getCurrentDashboardMonthValue,
  resolveDashboardMonthValue,
} from "../utils/dashboardMonth.helpers";
import { resolveBranchSiteAccess } from "../utils/esgByBranch.helpers";
import { toLngLat } from "../utils/geo";
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from "../utils/helpGuideSession";
import { formatMonth } from "../utils/numberFormat";
import { getEsgBranchWasteExport } from "../services/esg-dashboard.service";
import {
  buildBranchWasteExportFilename,
  buildBranchWasteExportWorkbook,
  downloadXlsxBuffer,
} from "../utils/branchWasteExportXlsx.helpers";

const UNASSIGNED_SITE_ID = "__unassigned__";

type HelpOverviewPhase = "month" | "stats" | "branches";

function filterBranchesBySiteAccess(
  branches: BranchOperationalRow[],
  unassigned: BranchOperationalRow | null,
  sitesAccess: string[] | "all" | "none",
): BranchOperationalRow[] {
  const access = resolveBranchSiteAccess(sitesAccess);

  if (access === "none") {
    return [];
  }

  const rows = [...branches];
  if (unassigned && access === "all") {
    rows.push(unassigned);
  }

  if (access === "all") {
    return rows;
  }

  const allowed = new Set(access);
  const filtered = branches.filter((row) => allowed.has(row.collection_site_id));
  if (unassigned && allowed.has(UNASSIGNED_SITE_ID)) {
    filtered.push(unassigned);
  }
  return filtered;
}

const BranchAnalysis: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { t: tHelp } = useTranslation("translation", { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization } = useOrganizationStore();
  const { sitesForOrg, can } = useAuthPermissions();
  const {
    byBranch,
    isLoadingByBranch,
    byBranchError,
    selectedMonth,
    fetchDashboardByBranch,
    setSelectedMonth,
    loadDashboardPreferences,
  } = useDashboardStore();
  const { isStep, tourPhase, setTourPhase, completeStep, dismissTour } =
    useSelfSetupTour();
  const showBranchTour = isStep("branchAnalysis");

  const [helpOverview, setHelpOverview] = useState(false);
  const [helpOverviewPhase, setHelpOverviewPhase] =
    useState<HelpOverviewPhase>("month");
  const [helpCompare, setHelpCompare] = useState(false);

  const showMonthTip =
    (showBranchTour && tourPhase === "monthSelect") ||
    (helpOverview && helpOverviewPhase === "month");
  const showStatsTip =
    (showBranchTour && tourPhase === "keyNumbers") ||
    (helpOverview && helpOverviewPhase === "stats");
  const showBranchesTip =
    (showBranchTour && tourPhase === "branches") ||
    (helpOverview && helpOverviewPhase === "branches");
  const showCompareTip = showBranchTour && tourPhase === "compare";
  const selfSetupCompareDialogPhase: BranchCompareTourPhase | null =
    tourPhase === "compareSelect"
      ? "select"
      : tourPhase === "compareResults"
        ? "results"
        : tourPhase === "compareExport"
          ? "export"
          : null;
  const showCompareDialogTour =
    (showBranchTour && selfSetupCompareDialogPhase != null) || helpCompare;

  const [isExportingXlsx, setIsExportingXlsx] = useState(false);
  const [exportXlsxError, setExportXlsxError] = useState<string | null>(null);

  const [compareOpen, setCompareOpen] = useState(false);
  const [compareDialogPhase, setCompareDialogPhase] =
    useState<BranchCompareTourPhase>("select");

  const activeCompareDialogPhase: BranchCompareTourPhase =
    selfSetupCompareDialogPhase ?? compareDialogPhase;

  const availableMonths = useMemo(
    () => [
      ...buildDashboardMonthOptions(),
      { value: ALL_TIME_MONTH_VALUE, label: t("branchAnalysis.allTime") },
    ],
    [t],
  );
  const monthValue = resolveDashboardMonthValue(selectedMonth, availableMonths);
  const isAllTime = monthValue === ALL_TIME_MONTH_VALUE;

  const isWasteOutlet =
    selectedOrganization?.organization_type === OrganizationType.WASTE_OUTLET;

  useEffect(() => {
    loadDashboardPreferences();
  }, [loadDashboardPreferences]);

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get("helpGuide");
    if (
      guide !== HELP_GUIDE_BRANCH_ANALYSIS &&
      guide !== HELP_GUIDE_COMPARE_BRANCHES
    ) {
      return;
    }
    markHelpGuideActive(guide);
    if (guide === HELP_GUIDE_BRANCH_ANALYSIS) {
      setHelpOverview(true);
      setHelpOverviewPhase("month");
    } else {
      setHelpCompare(true);
      setCompareOpen(true);
      setCompareDialogPhase("select");
    }
    navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!selectedOrganization?.id || !isWasteOutlet) return;

    const resolved = resolveDashboardMonthValue(selectedMonth, availableMonths);
    if (resolved !== selectedMonth) {
      setSelectedMonth(resolved);
      return;
    }

    fetchDashboardByBranch(selectedOrganization.id, resolved);
  }, [
    selectedOrganization?.id,
    selectedOrganization?.organization_type,
    selectedMonth,
    isWasteOutlet,
    fetchDashboardByBranch,
    setSelectedMonth,
    availableMonths,
  ]);

  useEffect(() => {
    if (!showMonthTip && !showStatsTip && !showBranchesTip && !showCompareTip) {
      return;
    }
    const id = showMonthTip
      ? "self-setup-branch-month"
      : showStatsTip
        ? "self-setup-branch-stats"
        : showBranchesTip
          ? "self-setup-branch-cards"
          : "self-setup-branch-compare";
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [showMonthTip, showStatsTip, showBranchesTip, showCompareTip]);

  useEffect(() => {
    if (showBranchesTip) setBranchView("table");
  }, [showBranchesTip]);

  // Keep compare dialog open while its self-setup tips are active (incl. resume).
  useEffect(() => {
    if (!showBranchTour) return;
    if (
      tourPhase === "compareSelect" ||
      tourPhase === "compareResults" ||
      tourPhase === "compareExport"
    ) {
      setCompareOpen(true);
    }
  }, [showBranchTour, tourPhase]);

  const handleMonthChange = (value: string) => {
    setSelectedMonth(value);
  };

  const handleExportXlsx = async () => {
    if (!selectedOrganization?.id) return;
    setIsExportingXlsx(true);
    setExportXlsxError(null);
    try {
      const exportData = await getEsgBranchWasteExport(
        "waste-outlet",
        selectedOrganization.id,
      );
      const buffer = await buildBranchWasteExportWorkbook(exportData);
      downloadXlsxBuffer(
        buffer,
        buildBranchWasteExportFilename(selectedOrganization.name ?? ""),
      );
    } catch (error) {
      setExportXlsxError(
        error instanceof Error ? error.message : t("branchAnalysis.export.error"),
      );
    } finally {
      setIsExportingXlsx(false);
    }
  };

  const openCompareDialog = () => {
    setCompareOpen(true);
    if (showBranchTour && tourPhase === "compare") {
      setTourPhase("compareSelect");
    }
    if (helpCompare) {
      setCompareDialogPhase("select");
    }
  };

  const closeCompareDialog = () => {
    setCompareOpen(false);
    if (
      showBranchTour &&
      (tourPhase === "compareSelect" ||
        tourPhase === "compareResults" ||
        tourPhase === "compareExport")
    ) {
      setTourPhase("compare");
    }
    if (helpCompare) {
      setHelpCompare(false);
      clearHelpGuideActive();
      setCompareDialogPhase("select");
    }
  };

  const finishHelpOverview = () => {
    setHelpOverview(false);
    setHelpOverviewPhase("month");
    clearHelpGuideActive();
  };

  const finishHelpCompare = () => {
    setCompareOpen(false);
    setHelpCompare(false);
    setCompareDialogPhase("select");
    clearHelpGuideActive();
  };

  const advanceBranchTip = () => {
    if (helpOverview) {
      if (helpOverviewPhase === "month") {
        setHelpOverviewPhase("stats");
        return;
      }
      if (helpOverviewPhase === "stats") {
        setHelpOverviewPhase("branches");
        return;
      }
      finishHelpOverview();
      return;
    }
    if (tourPhase === "monthSelect") {
      setTourPhase("keyNumbers");
      return;
    }
    if (tourPhase === "keyNumbers") {
      setTourPhase("branches");
      return;
    }
    if (tourPhase === "branches") {
      setTourPhase("compare");
      return;
    }
    if (tourPhase === "compare") {
      openCompareDialog();
      return;
    }
    completeStep("branchAnalysis");
  };

  const advanceCompareDialogTour = () => {
    if (showBranchTour && selfSetupCompareDialogPhase) {
      if (tourPhase === "compareSelect") {
        setTourPhase("compareResults");
        return;
      }
      if (tourPhase === "compareResults") {
        setTourPhase("compareExport");
        return;
      }
      setCompareOpen(false);
      completeStep("branchAnalysis");
      return;
    }
    if (compareDialogPhase === "select") {
      setCompareDialogPhase("results");
      return;
    }
    if (compareDialogPhase === "results") {
      setCompareDialogPhase("export");
      return;
    }
    if (helpCompare) {
      finishHelpCompare();
      return;
    }
    setCompareOpen(false);
    completeStep("branchAnalysis");
  };

  const finishBranchTip = () => {
    if (helpOverview) {
      finishHelpOverview();
      return;
    }
    if (helpCompare) {
      finishHelpCompare();
      return;
    }
    if (showBranchTour) {
      // Keep current tourPhase so dismissTour saves exact tip (incl. compare*).
      setCompareOpen(false);
      dismissTour();
      return;
    }
    dismissTour();
  };

  const sitesAccess = selectedOrganization?.id
    ? sitesForOrg(selectedOrganization.id)
    : "none";

  const branchRows = useMemo(() => {
    if (!byBranch) return [];
    return filterBranchesBySiteAccess(
      byBranch.branches ?? [],
      byBranch.unassigned_waste,
      sitesAccess,
    );
  }, [byBranch, sitesAccess]);

  const monthTipTitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisMonthTitle")
    : t("selfSetup.tour.branchMonthTitle");
  const monthTipSubtitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisMonthSubtitle")
    : t("selfSetup.tour.branchMonthSubtitle");
  const statsTipTitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisStatsTitle")
    : t("selfSetup.tour.branchStatsTitle");
  const statsTipSubtitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisStatsSubtitle")
    : t("selfSetup.tour.branchStatsSubtitle");
  const cardsTipTitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisCardsTitle")
    : t("selfSetup.tour.branchCardsTitle");
  const cardsTipSubtitle = helpOverview
    ? tHelp("knowledgeBase.branchAnalysisCardsSubtitle")
    : t("selfSetup.tour.branchCardsSubtitle");
  const compareTourGuide = showCompareDialogTour
    ? {
        active: true as const,
        phase: activeCompareDialogPhase,
        onDismiss: finishBranchTip,
        onNext: advanceCompareDialogTour,
        selectTitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesSelectTitle")
          : t("selfSetup.tour.branchCompareSelectTitle"),
        selectSubtitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesSelectSubtitle")
          : t("selfSetup.tour.branchCompareSelectSubtitle"),
        resultsTitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesResultsTitle")
          : t("selfSetup.tour.branchCompareResultsTitle"),
        resultsSubtitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesResultsSubtitle")
          : t("selfSetup.tour.branchCompareResultsSubtitle"),
        exportTitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesExportTitle")
          : t("selfSetup.tour.branchCompareExportTitle"),
        exportSubtitle: helpCompare
          ? tHelp("knowledgeBase.compareBranchesExportSubtitle")
          : t("selfSetup.tour.branchCompareExportSubtitle"),
        nextLabel: t("selfSetup.tourNext"),
        doneLabel: t("selfSetup.tourDone"),
      }
    : null;
  const fmt = useNumberFormat();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  /** Branches section view; the map opens first. */
  const [branchView, setBranchView] = useState<"table" | "map">("map");

  // Completion rate per branch from the period's pickup orders (the by-branch
  // API has no order counts). Shown as "—" without the permission, or when
  // the list is too long to page through in full.
  const orgIdForOrders = selectedOrganization?.id ?? null;
  const canListOrders = Boolean(orgIdForOrders) && can(orgScopedPermission(orgIdForOrders as string, "pickup_order", "list"));
  const ordersMonthStart = useMemo(() => {
    const match = /^(\d{4})-(\d{2})/.exec(monthValue);
    return isAllTime || !match ? null : new Date(Number(match[1]), Number(match[2]) - 1, 1);
  }, [monthValue, isAllTime]);
  const branchOrders = useMonthPickupOrders(orgIdForOrders, ordersMonthStart, null, isWasteOutlet && canListOrders);
  const orderCompletion = useMemo(
    () =>
      branchOrders.status === "ok" && !branchOrders.data.truncated
        ? orderCompletionBySite(branchOrders.data.orders, ordersMonthStart, UNASSIGNED_SITE_ID)
        : null,
    [branchOrders.status, branchOrders.data, ordersMonthStart],
  );
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

  if (!selectedOrganization?.id) {
    return <EmptyState icon={ZERO_ICONS.branch} title={t("branchAnalysis.selectOrganization")} minHeight={240} />;
  }

  if (!isWasteOutlet) {
    return <EmptyState icon={ZERO_ICONS.branch} title={t("branchAnalysis.wrongOrgType")} minHeight={240} />;
  }

  const monthLabel = (value: string) => {
    if (value === ALL_TIME_MONTH_VALUE) return t("branchAnalysis.allTime");
    const match = /^(\d{4})-(\d{2})/.exec(value);
    return match ? formatMonth(new Date(Number(match[1]), Number(match[2]) - 1, 1), i18n.language) : value;
  };
  const now = new Date();
  const isCurrentMonth =
    !isAllTime && monthValue.startsWith(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const showDeltas = !isAllTime && !isCurrentMonth;
  const deltaOf = (current: number | null | undefined, previous: number | null | undefined, points = false) => {
    if (!showDeltas || typeof current !== "number" || typeof previous !== "number" || previous === 0) return undefined;
    return {
      value: points ? current - previous : ((current - previous) / Math.abs(previous)) * 100,
      kind: points ? ("points" as const) : ("relative" as const),
      polarity: "higher_is_better" as const,
    };
  };

  const toggleRow = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectedBranchId(id);
  };
  const showOnMap = (id: string) => {
    setSelectedBranchId(id);
    setBranchView("map");
  };
  // A marker opens the branch's row in the table.
  const onMapSelect = (markerId: string) => {
    const id = markerId.replace(/^branch:/, "");
    setSelectedBranchId(id);
    setExpandedIds((prev) => new Set(prev).add(id));
    setBranchView("table");
    window.setTimeout(
      () => document.getElementById(`branch-row-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }),
      80,
    );
  };

  const located = branchRows
    .filter((row) => row.collection_site_id !== UNASSIGNED_SITE_ID)
    .flatMap((row) => {
      const position = toLngLat(row.latitude, row.longitude);
      return position ? [{ row, position }] : [];
    });
  const markers: MapMarker[] = located.map(({ row, position }) => {
    const selected = selectedBranchId === row.collection_site_id;
    return {
      id: `branch:${row.collection_site_id}`,
      position,
      tone: selected ? "active" : "complete",
      label: row.name?.trim() || t("branchAnalysis.unnamedBranch"),
      glyph: "branch",
      shape: "square",
      state: selected ? "selected" : selectedBranchId ? "dimmed" : "default",
      raised: selected,
    };
  });
  const selectedPosition = located.find(({ row }) => row.collection_site_id === selectedBranchId)?.position;
  const totalCollected = branchRows.reduce((sum, row) => sum + (row.total_waste_kg ?? 0), 0);

  const monthSelect = (
    <Select
      id="self-setup-branch-month"
      size="small"
      value={monthValue}
      onChange={(event) => handleMonthChange(event.target.value)}
      aria-label={t("branchAnalysis.period")}
      startAdornment={<ZERO_ICONS.period sx={{ fontSize: 18, color: "text.secondary", marginInlineEnd: 1 }} />}
      sx={{ minWidth: 200 }}
      renderValue={(value) => monthLabel(String(value) || getCurrentDashboardMonthValue())}
    >
      {availableMonths.map((month) => (
        <MenuItem key={month.value} value={month.value}>
          {monthLabel(month.value)}
        </MenuItem>
      ))}
    </Select>
  );

  const compareButton = (
    <Button
      id="self-setup-branch-compare"
      variant="outlined"
      color="inherit"
      startIcon={<ZERO_ICONS.compare />}
      onClick={openCompareDialog}
      disabled={branchRows.length < 2 || isLoadingByBranch}
      sx={{ whiteSpace: "nowrap" }}
    >
      {t("branchAnalysis.compare.button")}
    </Button>
  );

  const exportButton = (
    <Button
      id="self-setup-branch-export"
      variant="outlined"
      color="inherit"
      startIcon={<ZERO_ICONS.download />}
      onClick={handleExportXlsx}
      disabled={isExportingXlsx}
      sx={{ whiteSpace: "nowrap" }}
    >
      {isExportingXlsx ? t("branchAnalysis.export.exporting") : t("branchAnalysis.export.button")}
    </Button>
  );

  const summary = byBranch ? (
    <MetricStrip columns={{ xs: 2, sm: 3, lg: 5 }}>
      <KpiFigure
        icon={ZERO_ICONS.collections}
        label={t("branchAnalysis.summaryCards.pickupOrders")}
        value={fmt.number(byBranch.total_pickup_orders)}
        size="lg"
        delta={deltaOf(byBranch.total_pickup_orders, byBranch.previous_month_total_pickup_orders)}
      />
      <KpiFigure
        icon={ZERO_ICONS.mass}
        label={t("branchAnalysis.summaryCards.totalWaste")}
        value={fmt.metric("mass_kg", byBranch.total_waste_kg, { scaleMass: true }).text}
        unit={fmt.metric("mass_kg", byBranch.total_waste_kg, { scaleMass: true }).unit}
        size="md"
        delta={deltaOf(byBranch.total_waste_kg, byBranch.previous_month_total_waste_kg)}
      />
      <KpiFigure
        icon={ZERO_ICONS.waste}
        label={t("branchAnalysis.summaryCards.completedWaste")}
        value={fmt.metric("mass_kg", byBranch.completed_waste_kg, { scaleMass: true }).text}
        unit={fmt.metric("mass_kg", byBranch.completed_waste_kg, { scaleMass: true }).unit}
        size="md"
        delta={deltaOf(byBranch.completed_waste_kg, byBranch.previous_month_completed_waste_kg)}
      />
      <KpiFigure
        icon={ZERO_ICONS.revenue}
        label={t("branchAnalysis.summaryCards.revenue")}
        value={fmt.metric("sar", byBranch.total_revenue).text}
        unit={t("units.sar")}
        size="md"
        delta={deltaOf(byBranch.total_revenue, byBranch.previous_month_total_revenue)}
      />
      <KpiFigure
        icon={ZERO_ICONS.rate}
        label={t("branchAnalysis.summaryCards.diversionRate")}
        value={fmt.number(byBranch.waste_diversion_rate_percent, { maximumFractionDigits: 1 })}
        unit="%"
        size="md"
        delta={deltaOf(byBranch.waste_diversion_rate_percent, byBranch.previous_month_waste_diversion_rate_percent, true)}
      />
    </MetricStrip>
  ) : null;

  return (
    <Box>
      <PageHeader
        icon={<ZERO_ICONS.branch />}
        title={t("branchAnalysis.title")}
        subtitle={t("branchAnalysis.subtitle", { name: selectedOrganization.name })}
        actions={
          <>
            <SetupCoachmark
              open={showMonthTip}
              title={monthTipTitle}
              subtitle={monthTipSubtitle}
              onDismiss={finishBranchTip}
              onNext={advanceBranchTip}
              nextLabel={t("selfSetup.tourNext")}
            >
              {monthSelect}
            </SetupCoachmark>
            {!showMonthTip ? (
              <SetupCoachmark
                open={showCompareTip}
                title={t("selfSetup.tour.branchCompareTitle")}
                subtitle={t("selfSetup.tour.branchCompareSubtitle")}
                onDismiss={finishBranchTip}
                onNext={advanceBranchTip}
                nextLabel={t("selfSetup.tourNext")}
              >
                {compareButton}
              </SetupCoachmark>
            ) : (
              compareButton
            )}
            {exportButton}
          </>
        }
      />

      {exportXlsxError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportXlsxError(null)}>
          {exportXlsxError}
        </Alert>
      )}

      <BranchComparisonDialog
        open={compareOpen}
        onClose={closeCompareDialog}
        branches={branchRows}
        organizationName={selectedOrganization?.name ?? ""}
        organizationLogoUrl={selectedOrganization?.logo_url ?? null}
        selectedMonth={monthValue}
        tourGuide={compareTourGuide}
      />

      {byBranchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("branchAnalysis.errorLoading", { error: byBranchError })}
        </Alert>
      )}

      <SetupCoachmark
        open={showStatsTip}
        title={statsTipTitle}
        subtitle={statsTipSubtitle}
        onDismiss={finishBranchTip}
        onNext={advanceBranchTip}
        nextLabel={t("selfSetup.tourNext")}
        fullWidth
      >
        <Box id="self-setup-branch-stats" component="section" sx={{ width: "100%", mb: { xs: 4, md: 5 } }}>
          <SectionHeader
            title={t("branchAnalysis.summaryTitle", { period: monthLabel(monthValue) })}
            icon={ZERO_ICONS.collections}
          />
          {isLoadingByBranch || !summary ? <Skeleton variant="rounded" height={132} /> : summary}
        </Box>
      </SetupCoachmark>

      <Box component="section">
        <SectionHeader
          title={t("branchAnalysis.table.title")}
          icon={ZERO_ICONS.branch}
          description={t("branchAnalysis.table.description")}
        />
        <Box
          sx={(theme) => ({
            bgcolor: "background.paper",
            border: `1px solid ${contentBorders(theme).default}`,
            borderRadius: "8px",
            overflow: "hidden",
          })}
        >
          <Tabs
            value={branchView}
            onChange={(_, v: "table" | "map") => setBranchView(v)}
            aria-label={t("branchAnalysis.table.title")}
            sx={(theme) => ({ px: 1, minHeight: 44, borderBottom: `1px solid ${contentBorders(theme).default}` })}
          >
            <Tab value="table" label={t("branchAnalysis.view.table")} icon={<TableRowsOutlined fontSize="small" />} iconPosition="start" sx={{ minHeight: 44 }} />
            <Tab value="map" label={t("branchAnalysis.view.map")} icon={<ZERO_ICONS.map fontSize="small" />} iconPosition="start" sx={{ minHeight: 44 }} />
          </Tabs>
          {branchView === "table" ? (
            <SetupCoachmark
              open={showBranchesTip}
              title={cardsTipTitle}
              subtitle={cardsTipSubtitle}
              onDismiss={finishBranchTip}
              onNext={advanceBranchTip}
              nextLabel={helpOverview ? t("selfSetup.tourDone") : t("selfSetup.tourNext")}
              fullWidth
            >
              <Box id="self-setup-branch-cards" sx={{ width: "100%", minWidth: 0 }}>
                {isLoadingByBranch ? (
                  <Skeleton variant="rounded" height={320} />
                ) : branchRows.length === 0 ? (
                  <EmptyState icon={ZERO_ICONS.branch} title={t("branchAnalysis.empty")} minHeight={240} />
                ) : (
                  <BranchTable
                    rows={branchRows}
                    totalCollectedKg={totalCollected}
                    expandedIds={expandedIds}
                    onToggle={toggleRow}
                    selectedId={selectedBranchId}
                    onShowOnMap={showOnMap}
                    orderCompletion={orderCompletion}
                    orderCompletionLoading={branchOrders.status === "loading"}
                  />
                )}
              </Box>
            </SetupCoachmark>
          ) : (
            <Box id="branch-map" sx={{ height: { xs: 360, md: 520, lg: 600 } }}>
              <Suspense fallback={<Skeleton variant="rectangular" height="100%" />}>
                <LazyZeroMap
                  ariaLabel={t("branchAnalysis.mapLabel")}
                  markers={markers}
                  onSelect={onMapSelect}
                  fitKey={`${monthValue}|${markers.length}|${selectedBranchId ?? ""}`}
                  fitTo={selectedPosition ? [selectedPosition] : undefined}
                  sx={{ height: "100%" }}
                />
              </Suspense>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BranchAnalysis;
